import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import type { Insured } from "@shared/schema";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StateSelect } from "@/components/state-select";
import { useToast } from "@/hooks/use-toast";
import { useUpdateInsured } from "@/lib/api";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  insured: Insured;
  kind: "physical" | "mailing";
}

interface FormErrors {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export function EditAddressDialog({ open, onOpenChange, insured, kind }: Props) {
  const { toast } = useToast();
  const updateInsured = useUpdateInsured();

  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [country, setCountry] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (!open) return;
    if (kind === "physical") {
      setStreet(insured.phyStreet ?? "");
      setCity(insured.phyCity ?? "");
      setState(insured.phyState ?? "");
      setZip(insured.phyZip ?? "");
      setCountry(insured.phyCountry ?? "");
    } else {
      setStreet(insured.mailingStreet ?? "");
      setCity(insured.mailingCity ?? "");
      setState(insured.mailingState ?? "");
      setZip(insured.mailingZip ?? "");
      setCountry(insured.mailingCountry ?? "");
    }
    setErrors({});
  }, [open, insured, kind]);

  function handleSubmit() {
    const next: FormErrors = {};
    if (!street.trim()) next.street = "Street is required.";
    if (!city.trim()) next.city = "City is required.";
    if (!state.trim()) next.state = "State is required.";
    if (!zip.trim()) next.zip = "ZIP is required.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const data =
      kind === "physical"
        ? {
            phyStreet: street.trim(),
            phyCity: city.trim(),
            phyState: state.trim().toUpperCase(),
            phyZip: zip.trim(),
            phyCountry: country.trim() || "US",
          }
        : {
            mailingStreet: street.trim(),
            mailingCity: city.trim(),
            mailingState: state.trim().toUpperCase(),
            mailingZip: zip.trim(),
            mailingCountry: country.trim() || "US",
          };

    updateInsured
      .mutateAsync({ id: insured.id, data })
      .then(() => {
        toast({ title: "Saved" });
        onOpenChange(false);
      })
      .catch((err) => {
        toast({
          title: "Failed",
          description: err instanceof Error ? err.message : "Could not save address.",
          variant: "destructive",
        });
      });
  }

  const title = kind === "physical" ? "Edit Policy / Garaging Address" : "Edit Mailing Address";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="address-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            This address syncs from FMCSA / carrier feeds — edits apply immediately and may be
            overwritten on next sync.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="address-street">Street *</Label>
            <Input
              id="address-street"
              data-testid="address-street-input"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              className={errors.street ? "border-destructive" : ""}
            />
            {errors.street && <p className="text-xs text-destructive mt-1">{errors.street}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="address-city">City *</Label>
              <Input
                id="address-city"
                data-testid="address-city-input"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className={errors.city ? "border-destructive" : ""}
              />
              {errors.city && <p className="text-xs text-destructive mt-1">{errors.city}</p>}
            </div>
            <div>
              <Label htmlFor="address-state">State *</Label>
              <StateSelect
                id="address-state"
                value={state}
                onChange={setState}
                className={errors.state ? "border-destructive" : ""}
              />
              {errors.state && <p className="text-xs text-destructive mt-1">{errors.state}</p>}
            </div>
            <div>
              <Label htmlFor="address-zip">ZIP *</Label>
              <Input
                id="address-zip"
                data-testid="address-zip-input"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                className={errors.zip ? "border-destructive" : ""}
              />
              {errors.zip && <p className="text-xs text-destructive mt-1">{errors.zip}</p>}
            </div>
            <div>
              <Label htmlFor="address-country">Country</Label>
              <Input
                id="address-country"
                data-testid="address-country-input"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="US"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateInsured.isPending}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={updateInsured.isPending}
            data-testid="address-dialog-submit"
            className="cursor-pointer"
          >
            {updateInsured.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
